import React, { useEffect, useState } from "react"
import { useIntl } from "react-intl"
import {
	DataTableCellAlphaNumeric,
	DataTableRow,
} from "@walmart-web/livingdesign-components"
import InformationTable from "components/Shared/InformationTable"
import PointsCell from "components/Shared/Table/PointsCell"
import { ChartCell } from "components/Shared/Table/ChartCell"
import { getMaxValue, COLORS } from "components/Shared/Table/helpers"
import Legend from "components/Transactional/Performance/components/Legend/Legend"
import { useFiltersContext } from "components/Shared/GlobalFilter/context/filtersContext"
import { getDatesFromRanges } from "components/Shared/PhronesisCalendar/PhronesisCalendar.utils"
import Paginator, { usePaginationLocal } from "components/Shared/Paginator"
import { getVSMBreakdown } from "./service"
import AsyncMessageResponseHandler from "components/Shared/AsyncMessageResponseHandler"
import SkeletonWrapper from "components/Shared/SkeletonWrapper"
import { BenchmarkTableSkeleton } from "components/Transactional/Performance/components"
import { useFetchData } from "components/Shared/Hooks/react-query/useFetchData"
import { DEFAULT_SORTING } from "components/Transactional/Performance/constants"
import { ITEMS_PER_PAGE, RETRIES } from "constants/constants"
import { default as LegendTable } from "components/CustomerBehavior/LoyaltyPerformance/Loyalty/TableSummary/Legend"
import { titleCapitalize } from "helpers/text"
import { formatNumber } from "helpers/numbers"
import {
	columnsHeader,
	mustShowMoreColumns,
	VSMData,
	VSMResponse,
} from "./utils"
import { getLevel, TabOptions } from "../utils"
import "./index.scss"
import { ExportCSV } from "components/Shared/ExportCSV"
import { truncate } from "components/Shared/Hooks/useFormatters/useFormaters"

interface TableProps {
	tab: TabOptions
}

const Rows: React.FC<{
	currentData: VSMData[]
	tab: TabOptions
	data: VSMData[]
	preset: string
}> = ({ currentData, tab, data, preset }) => {
	const extraColumns = mustShowMoreColumns(tab)
	const maxValue = getMaxValue(data, ["currentSales", "previousSales"])
	const level = getLevel(tab)
	const intl = useIntl()
	return (
		<React.Fragment>
			{currentData.map((item) => {
				const rowData = [
					{
						previous: item.previousSales,
						actual: item.currentSales,
					},
				]
				return (
					<DataTableRow key={item.id}>
						<DataTableCellAlphaNumeric>
							{titleCapitalize(item.name)}
						</DataTableCellAlphaNumeric>
						{extraColumns &&
							(level === "products" ? (
								<React.Fragment>
									<DataTableCellAlphaNumeric>
										{titleCapitalize(item.subcategoryName)}
									</DataTableCellAlphaNumeric>
									<DataTableCellAlphaNumeric>
										{item.barcode}
									</DataTableCellAlphaNumeric>
								</React.Fragment>
							) : (
								<React.Fragment>
									<DataTableCellAlphaNumeric>
										{item.id}
									</DataTableCellAlphaNumeric>
								</React.Fragment>
							))}
						<ChartCell
							id={`chart-${item.id}`}
							data={rowData}
							keys={["actual", "previous"]}
							innerPadding={2}
							colors={COLORS}
							maxValue={maxValue}
						>
							<LegendTable
								label={intl.formatMessage(
									{
										id:
											"phronesis.transactional.application.commercial.vsm.legend.tooltip",
									},
									{
										preset,
										period: "previous",
										quantity: formatNumber(item.previousSales, intl),
										b: (text: string) => <b>{text}</b>,
									}
								)}
								color={COLORS[1]}
							/>
							<LegendTable
								label={intl.formatMessage(
									{
										id:
											"phronesis.transactional.application.commercial.vsm.legend.tooltip",
									},
									{
										preset,
										period: "current",
										quantity: formatNumber(item.currentSales, intl),
										b: (text: string) => <b>{text}</b>,
									}
								)}
								color={COLORS[0]}
							/>
						</ChartCell>
						<PointsCell isPercentage>
							{truncate(item.penetrationRate, 4)}
						</PointsCell>
						<PointsCell isPercentage>
							{truncate(item.closingRate, 4)}
						</PointsCell>
						<PointsCell>{truncate(item.spendingRate, 4)}</PointsCell>
					</DataTableRow>
				)
			})}
		</React.Fragment>
	)
}

const Table: React.FC<TableProps> = (props) => {
	const { filters, preset, interval } = useFiltersContext()

	const {
		data = { breakdown: { data: [] } },
		error,
		isError,
		isLoading,
		refetch,
	} = useFetchData<VSMResponse>(
		`vsm-breakdown-${props.tab}`,
		getVSMBreakdown,
		{
			filters,
			interval: getDatesFromRanges(interval),
			metricName: `vsm_breakdown_by_${props.tab}`,
		},
		{
			enabled: true,
			retry: RETRIES,
		}
	)

	const [dataTable, setDataTable] = useState<VSMData[]>(data?.breakdown?.data)

	useEffect(() => {
		setDataTable(data?.breakdown?.data)
	}, [JSON.stringify(data)])

	const { currentData, getPaginatorProps } = usePaginationLocal({
		data: dataTable,
		itemsPerPage: ITEMS_PER_PAGE,
	})

	const intl = useIntl()

	const getExportDataFileName = () => {
		const module = intl.formatMessage({
			id:
				"phronesis.transactional.application.commercial.benchmark.tabs.value-shared-model",
		})
		const tab = intl.formatMessage(
			{
				id:
					"phronesis.transactional.application.commercial.benchmark.title.column.metric",
			},
			{ metric: getLevel(props.tab) }
		)
		return `${module}_${tab}`
	}

	return (
		<SkeletonWrapper
			isLoading={isLoading}
			skeleton={<BenchmarkTableSkeleton />}
		>
			<AsyncMessageResponseHandler
				className="error-card"
				hasData={Boolean(dataTable)}
				error={error}
				isLoading={isLoading}
				isError={isError}
				retry={refetch}
			>
				<div className="csv-button-container">
					{!!dataTable?.length && (
						<ExportCSV
							fileName={getExportDataFileName()}
							data={dataTable}
							name={
								"phronesis.transactional.application.cross-selling.download"
							}
							align={"right"}
						/>
					)}
				</div>
				<InformationTable
					id="value-shared-model"
					className="vsm-table"
					columnsHeader={columnsHeader(props.tab)}
					rowsBody={
						<Rows
							currentData={currentData}
							tab={props.tab}
							data={dataTable}
							preset={preset}
						/>
					}
					onSort={setDataTable}
					defaultSorting={DEFAULT_SORTING}
					contentFooterTable={
						<React.Fragment>
							<Legend
								filtersBenchmark={{
									period: preset,
									...getDatesFromRanges(interval),
								}}
							/>
							<Paginator {...getPaginatorProps()} />
						</React.Fragment>
					}
					data={dataTable}
					{...props}
				/>
			</AsyncMessageResponseHandler>
		</SkeletonWrapper>
	)
}

export default Table
